const cloudServiceStates = new Set([
  "NotActivated", "Trial", "Active", "Grace", "Suspended", "Cancelled", "Expired",
]);
// Must remain identical to CustomerServiceCapabilityPolicy.ActiveCloudCapabilities.
const activeCloudServiceStates = new Set(["Trial", "Active", "Grace"]);
const cloudDataAvailabilityStates = new Set(["NotCreated", "Active", "ReadOnlyRetention", "Purged"]);

const finiteCount = value => typeof value === "number" && Number.isInteger(value) && value >= 0;

// Mirrors CustomerCloudAccessPolicy for customer-facing Web gates. Identity, billing,
// support and Local Control survive independently; paid Cloud workspace reads and writes
// both fail closed unless the subscription and retained data are active.
export function customerCloudAccessPolicy(access, effectiveAt = Date.now()) {
  const entitlement = access?.entitlement;
  const cloudState = entitlement?.cloudState;
  const dataAvailability = access?.dataAvailability;
  const effectiveAtMs = effectiveAt instanceof Date
    ? effectiveAt.getTime()
    : typeof effectiveAt === "string"
      ? Date.parse(effectiveAt)
      : Number(effectiveAt);
  const verified = Boolean(entitlement)
    && cloudServiceStates.has(cloudState)
    && cloudDataAvailabilityStates.has(dataAvailability)
    && typeof entitlement.organizationId === "string"
    && entitlement.organizationId.trim().length > 0
    && Number.isFinite(effectiveAtMs);
  const graceEndMs = typeof entitlement?.graceEndExclusiveUtc === "string"
    ? Date.parse(entitlement.graceEndExclusiveUtc)
    : Number.NaN;
  const activeEndMs = typeof entitlement?.effectiveUntilUtc === "string"
    ? Date.parse(entitlement.effectiveUntilUtc)
    : Number.NaN;
  const activePeriod = !["Trial", "Active"].includes(cloudState)
    || (Number.isFinite(activeEndMs) && effectiveAtMs < activeEndMs);
  // Grace is end-exclusive. A missing, malformed, or elapsed deadline is never
  // cached authority for paid workspace reads or commands.
  const graceActive = cloudState !== "Grace"
    || (Number.isFinite(graceEndMs) && effectiveAtMs < graceEndMs);
  const active = verified && activeCloudServiceStates.has(cloudState) && activePeriod && graceActive;
  const resolvedCloudState = verified
    && ((cloudState === "Grace" && !graceActive) || (["Trial", "Active"].includes(cloudState) && !activePeriod))
    ? "Expired"
    : cloudState;

  return {
    verified,
    cloudState: verified ? resolvedCloudState : null,
    dataAvailability: verified ? dataAvailability : null,
    canSignIn: true,
    canReadCloudWorkspace: active && dataAvailability === "Active",
    canMutateCloudWorkspace: active && dataAvailability === "Active",
    requiresLocalRehydration: active && dataAvailability === "Purged",
  };
}

// Mirrors CustomerCloudChangePolicy funding: active included Cloud changes or a
// previously purchased Single Change can fund a metered publish/logo activation.
// An unavailable response is left unverified so the server remains authoritative.
export function customerCloudChangePolicy(entitlement) {
  if (!entitlement || typeof entitlement.subscriptionState !== "string")
    return { verified: false, allowed: null, funding: null, exhausted: false };

  const included = entitlement.includedChangesRemaining ??
    (finiteCount(entitlement.includedChanges) && finiteCount(entitlement.usedIncludedChanges)
      ? Math.max(0, Number(entitlement.includedChanges) - Number(entitlement.usedIncludedChanges))
      : null);
  const purchased = entitlement.purchasedSingleChangesRemaining;
  if (!finiteCount(included) || !finiteCount(purchased))
    return { verified: false, allowed: null, funding: null, exhausted: false };

  const activeSubscription = ["Trial", "Active", "Grace"].includes(entitlement.subscriptionState);
  if (activeSubscription && Number(included) > 0)
    return { verified: true, allowed: true, funding: "IncludedMonthlyChange", exhausted: false };
  if (activeSubscription && Number(purchased) > 0)
    return { verified: true, allowed: true, funding: "PurchasedSingleChange", exhausted: false };
  return { verified: true, allowed: false, funding: "PurchaseRequired", exhausted: true };
}
